import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {ERROR} from '@di-shared/const';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useTripDetailNa from '@di-shared/features/history/date/useTripDetail.na';
import {DrivingInfoTripContentProps} from '@di-shared/features/history/driving-info/DrivingInfoTripContent/index';
import {formatNumberWithLimitAndCommas} from '@di-shared/features/history/driving-info/useDrivingInfo.na';
import useAvailableComponentNa from '@di-shared/features/home/na/useAvailableComponent.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import {formatNumberWithCommas} from '@di-shared/utils';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';
import useUnitConverter, {DistanceUnitSymbol} from '@di-shared/utils/useUnitConverter';
import {DistanceUnitType} from '@shared/cci/features/const/environment';
import {isSameDate, parseDateWithFormat} from '@shared/dateUtils';

export default function ({vin, selectedDate}: DrivingInfoTripContentProps) {
  return (
    <ErrorBoundary fallback={<FallbackError selectedDate={selectedDate} />}>
      <Suspense fallback={<FallbackLoading />}>
        <Inner
          vin={vin}
          selectedDate={selectedDate}
        />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({vin, selectedDate}: DrivingInfoTripContentProps) {
  const {isEmptyTrip} = useTripDetailNa({vin, selectedDate});

  if (isEmptyTrip) {
    return <None selectedDate={selectedDate} />;
  }

  return (
    <Default
      vin={vin}
      selectedDate={selectedDate}
    />
  );
}

function None({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_no_driving')}</span>
        </div>
      </div>
    </>
  );
}

function Default({vin, selectedDate}: DrivingInfoTripContentProps) {
  const today = new Date();
  const {t} = useInferableTranslation();
  const navigate = useNavigate();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {checkAvailableComponent} = useAvailableComponentNa({vin});
  const {convertDistanceValue, convertDistanceSymbol, convertSpeedSymbol, convertSpeedValue, isDistanceLessThan} = useUnitConverter();
  const {distanceUnit: appDistanceUnitName} = environmentStore.getState();
  const {tripDetailData, totalDrivingHours, totalDrivingMinutes, formattedRecentTripDetail} = useTripDetailNa({
    vin,
    selectedDate,
  });

  const ignitionOnDate = parseDateWithFormat(formattedRecentTripDetail.ignOnTime);
  const ignitionOffDate = parseDateWithFormat(formattedRecentTripDetail.ignOffTime);

  if (!ignitionOnDate || !ignitionOffDate) {
    throw new ExpectedError('ignitionOnDate or ignitionOffDate error');
  }

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
        <dl className="driving_summary_list">
          <div
            className="driving_summary_item"
            role="text"
            aria-label={`${t('ctn_mytrips_time')} ${
              !totalDrivingHours && !totalDrivingMinutes
                ? t('imsi_less_minute')
                : [
                    totalDrivingHours && `${totalDrivingHours}${t('ctn_mytrips_time_h_01')}`,
                    totalDrivingMinutes && `${totalDrivingMinutes}${t('ctn_mytrips_time_min_01')}`,
                  ]
                    .filter(Boolean)
                    .join(' ')
            }`}
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_time')}</dt>
            <dd className="driving_summary_info">
              {!!totalDrivingHours && (
                <>
                  <span className="driving_summary_value">{totalDrivingHours}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
                </>
              )}
              {!!totalDrivingMinutes && (
                <>
                  <span className="driving_summary_value">{totalDrivingMinutes}</span>
                  <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
                </>
              )}
              {!totalDrivingHours && !totalDrivingMinutes && <span className="driving_summary_value">{t('imsi_less_minute')}</span>}
            </dd>
          </div>
          <div
            className="driving_summary_item"
            role="text"
            aria-label={`${t('ctn_mytrips_distance')} ${
              isDistanceLessThan(tripDetailData.summary.totalDrvDist)
                ? t('imsi_less_distance_$unit', {
                    unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                  })
                : formatNumberWithLimitAndCommas(convertDistanceValue(tripDetailData.summary.totalDrvDist)) +
                  convertDistanceSymbol(tripDetailData.summary.totalDrvDist)
            }`}
          >
            <dt className="driving_summary_title">{t('ctn_mytrips_distance')}</dt>
            <dd className="driving_summary_info">
              {isDistanceLessThan(tripDetailData.summary.totalDrvDist) ? (
                <span className="driving_summary_value">
                  {t('imsi_less_distance_$unit', {
                    unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                  })}
                </span>
              ) : (
                <>
                  <span className="driving_summary_value">
                    {formatNumberWithLimitAndCommas(convertDistanceValue(tripDetailData.summary.totalDrvDist))}
                  </span>
                  <span className="driving_summary_unit">{convertDistanceSymbol(tripDetailData.summary.totalDrvDist)}</span>
                </>
              )}
            </dd>
          </div>
          {checkAvailableComponent('SAFETY_SCORE') && (
            <div
              className="driving_summary_item"
              role="text"
              aria-label={`${t('ctn_mytrips_driving_safety_score')} ${formatNumberWithCommas(tripDetailData.summary.safetyDrvScore)}${t('ctn_mytrips_driving_safety_score')} ${
                tripDetailData.summary.safetyDrvScoreDiff >= 0
                  ? `${t('ctn_mytrips_recent_rise')} ${formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}`
                  : `${t('ctn_mytrips_recent_fall')} ${formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}`
              }`}
            >
              <dt className="driving_summary_title">{t('ctn_mytrips_driving_safety_score')}</dt>
              <dd className="driving_summary_info">
                {tripDetailData.summary.safetyDrvScoreDiff >= 0 ? (
                  <span className="driving_summary_compare type_up">
                    <span className="ic ic_di_count_up" />
                    <span className="driving_summary_value">
                      {formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}
                    </span>
                  </span>
                ) : (
                  <span className="driving_summary_compare type_down">
                    <span className="ic ic_di_count_down" />
                    <span className="driving_summary_value">
                      {formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScoreDiff))}
                    </span>
                  </span>
                )}
                <span className="driving_summary_value">{formatNumberWithCommas(Math.abs(tripDetailData.summary.safetyDrvScore))}</span>
                <span className="driving_summary_unit">{t('ctn_home_dss_points')}</span>
              </dd>
            </div>
          )}
        </dl>
      </div>
      <div className="trip_info type_badge">
        <span className="blind">{t('ctn_mytrips_detail_resize')}</span>
        <div className="trip_info_head">
          <span className="trip_info_item">
            {DashedDatetimeFormatter.formatRange(ignitionOnDate, ignitionOffDate, 'time').replace('~', '-')}
          </span>
          {formattedRecentTripDetail?.tags
            ?.filter(({tagType}) => tagType === 'BUSINESS')
            .map(({tagName}) => (
              <span
                className="badge badge_basic_container"
                key={tagName}
              >
                {tagName}
              </span>
            ))}
        </div>
      </div>
      <div className="statistics_record_area">
        <ul className="statistics_record_list">
          <li className="statistics_record_item">
            <span className="statistics_record_key">
              <span className="ic ic_time_16px" />
              {t('ctn_mytrips_time')}
            </span>
            <span className="statistics_record_val">
              {!formattedRecentTripDetail.intervalIgnitionHours && !formattedRecentTripDetail.intervalIgnitionMinutes && (
                <span className="statistics_record_unit">{t('imsi_less_minute')}</span>
              )}
              {!!formattedRecentTripDetail.intervalIgnitionHours && (
                <>
                  <span className="statistics_record_number">{formattedRecentTripDetail.intervalIgnitionHours}</span>
                  <span className="statistics_record_unit">{t('ctn_mytrips_time_h_01')}</span>
                </>
              )}
              {!!formattedRecentTripDetail.intervalIgnitionMinutes && (
                <>
                  <span className="statistics_record_number">{formattedRecentTripDetail.intervalIgnitionMinutes}</span>
                  <span className="statistics_record_unit">{t('ctn_mytrips_time_min_01')}</span>
                </>
              )}
            </span>
          </li>
          <li className="statistics_record_item">
            <span className="statistics_record_key">
              <span className="ic ic_drive_distance_16px" />
              {t('ctn_mytrips_distance')}
            </span>
            <span className="statistics_record_val">
              {isDistanceLessThan(formattedRecentTripDetail.drvDist) ? (
                <span className="statistics_record_unit">
                  {t('imsi_less_distance_$unit', {
                    unit: appDistanceUnitName === DistanceUnitType.KM ? DistanceUnitSymbol.KM : DistanceUnitSymbol.MILE,
                  })}
                </span>
              ) : (
                <>
                  <span className="statistics_record_number">
                    {formatNumberWithLimitAndCommas(convertDistanceValue(formattedRecentTripDetail.drvDist))}
                  </span>
                  <span className="statistics_record_unit">{convertDistanceSymbol(formattedRecentTripDetail.drvDist)}</span>
                </>
              )}
            </span>
          </li>
          <li className="statistics_record_item">
            <span className="statistics_record_key">
              <span className="ic ic_avgspeed_16px" />
              {t('ctn_mytrips_details_avg_speed')}
            </span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">
                {formatNumberWithCommas(convertSpeedValue(formattedRecentTripDetail.avgSpeed))}
              </span>
              <span className="statistics_record_unit">{convertSpeedSymbol(formattedRecentTripDetail.avgSpeed)}</span>
            </span>
          </li>
        </ul>
        <button
          type="button"
          className="btn_learn_more"
          onClick={() => navigate(ROUTE_PATHS.HISTORY_$TRIP_ID.getFullPath(vin, formattedRecentTripDetail.id.toString()))}
        >
          {t('msg_mytrips_see_more_eu')}
          <span className="ic ic_arrow_forward_12px" />
        </button>
      </div>
    </>
  );
}

function FallbackLoading() {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();
  const {defaultDistanceSymbol, defaultSpeedSymbol} = useUnitConverter();
  const today = new Date();

  return (
    <>
      <div
        className="driving_summary skeleton"
        aria-hidden="true"
      >
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(today, 'MMMMEEEEd')}</span>
          <span className="date_today">{t('ctn_mytrips_today')}</span>
        </div>
        <dl className="driving_summary_list skeleton">
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('ctn_mytrips_time')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{t('ctn_mytrips_time_min_01')}</span>
            </dd>
          </div>
          <div className="driving_summary_item">
            <dt className="driving_summary_title">{t('ctn_mytrips_distance')}</dt>
            <dd className="driving_summary_info">
              <span className="driving_summary_value">{ERROR.DASH}</span>
              <span className="driving_summary_unit">{defaultDistanceSymbol}</span>
            </dd>
          </div>
        </dl>
      </div>
      <div
        className="trip_info skeleton"
        aria-hidden="true"
      >
        <div className="trip_info_head">
          <span className="trip_info_item">{DashedDatetimeFormatter.formatRange(today, today, 'time')}</span>
        </div>
      </div>
      <div className="statistics_record_area">
        <ul
          className="statistics_record_list skeleton"
          aria-hidden="true"
        >
          <li className="statistics_record_item">
            <span className="statistics_record_key">{t('ctn_mytrips_time')}</span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{t('ctn_mytrips_time_h_01')}</span>
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{t('ctn_mytrips_time_min_01')}</span>
            </span>
          </li>
          <li className="statistics_record_item">
            <span className="statistics_record_key">{t('ctn_mytrips_distance')}</span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{defaultDistanceSymbol}</span>
            </span>
          </li>
          <li className="statistics_record_item">
            <span className="statistics_record_key">{t('ctn_mytrips_details_avg_speed')}</span>
            <span className="statistics_record_val">
              <span className="statistics_record_number">{ERROR.DASH}</span>
              <span className="statistics_record_unit">{defaultSpeedSymbol}</span>
            </span>
          </li>
        </ul>
        <button
          type="button"
          className="btn_learn_more skeleton"
          aria-hidden="true"
        >
          {t('msg_mytrips_see_more_eu')}
          <span className="ic ic_arrow_forward_12px" />
        </button>
      </div>
    </>
  );
}

function FallbackError({selectedDate}: {selectedDate: Date}) {
  const {t} = useInferableTranslation();
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const today = new Date();

  return (
    <>
      <div className="driving_summary">
        <div className="driving_date">
          <span className="date_text">{CldrDatetimeFormatter.format(selectedDate, 'MMMMEEEEd')}</span>
          {isSameDate(today, selectedDate) && <span className="date_today">{t('ctn_mytrips_today')}</span>}
        </div>
      </div>
      <div className="trip_info">
        <div className="empty_area">
          <span className="ic ic_caution_32px" />
          <span className="body_small_r">{t('msg_mytrips_recent_load_data')}</span>
        </div>
      </div>
    </>
  );
}
